import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatRelative, parseJSON } from "date-fns";
import { Box, Grid, Image, ResponsiveContext, Stack, Text } from "grommet";
import { first, orderBy } from "lodash";
import * as React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TeamName } from "../teams/TeamName";
import { getMatch, MatchParticipantViewModel, MatchViewModel } from "./MatchesStore";
import { RenderOutcome } from "./OutcomeRenderer";
import { AliasTile } from "../aliases/AliasTile";

export const MatchRowWrapper = () => {
    let { id } = useParams<{ id: string }>();
    const [match, setMatch] = React.useState(new MatchViewModel());

    React.useEffect(() => {
        getMatch(id).then((match) => {
            setMatch(match);
            console.log("interactive");
        });
    }, []);

    return <MatchRow match={match}></MatchRow>;
};

export const MatchRow = ({ match }: { match: MatchViewModel }) => {
    let [teamOne, teamTwo] = match.participants as MatchParticipantViewModel[];
    let teamOneOutcome = first(orderBy((teamOne && teamOne.outcomes) || [], ["timestamp"], ["desc"]));
    let teamTwoOutcome = first(orderBy((teamTwo && teamTwo.outcomes) || [], ["timestamp"], ["desc"]));

    let navigate = useNavigate();

    const size = React.useContext(ResponsiveContext);
    
    // Determine if there's a winner
    const hasWinner = teamOneOutcome?.outcomeType === "Win" || teamTwoOutcome?.outcomeType === "Win";
    const teamOneWon = teamOneOutcome?.outcomeType === "Win";
    const teamTwoWon = teamTwoOutcome?.outcomeType === "Win";
    const navigationProps = match.id ? { hoverIndicator: "background-back", onClick: () => {} } : {};
    const linkProps = match.id ? { to: `/matches/${match.id}/` } : { to: "" };
    const GridProps =
        size == "small"
            ? {
                  rows: ["auto"],
                  columns: [["80px", "50%"], "100px", ["80px", "50%"]],
              }
            : { rows: ["auto"], columns: ["80px", ["80px", "50%"], "100px", ["80px", "50%"], "80px"] };

    return (
        <Link style={{ textDecoration: "none", color: "unset" }} {...linkProps}>
            <Stack anchor="top" interactiveChild={0}>
                <Box background={{ image: `url('${match.splashUri}')`, color: "background" }} pad={{ vertical: "xsmall", horizontal: "medium" }} {...navigationProps} style={{ height: "110px" }}>
                    {match.participants.length <= 2 && (
                        <Grid {...GridProps} fill>
                            {teamOne && (
                                <Box align="start" justify="center" fill alignContent="center">
                                    <img src={teamOne.team.logoUri} alt="" style={{ maxHeight: "60px" }} />
                                    {size == "small" && <TeamName team={teamOne.team} size={"medium"} color=""></TeamName>}
                                </Box>
                            )}
                            {size != "small" && teamOne && (
                                <Grid columns={{ count: 1, size: "auto" }} rows={[teamOne.aliases.length == 0 ? "40px" : "5px", "auto", teamOne.aliases.length == 0 ? "20px" : "65px"]}>
                                    <Box></Box>
                                    <TeamName team={teamOne.team} size={"medium"} color=""></TeamName>
                                    <Box direction="row" gap="small">
                                        {teamOne && teamOne.aliases.map((a) => <AliasTile key={a.aliasID} alias={a.alias} size="small"></AliasTile>)}
                                    </Box>
                                </Grid>
                            )}
                            <div></div>
                            {size != "small" && teamTwo && (
                                <Grid justify="end" columns={{ count: 1, size: "auto" }} rows={[teamTwo.aliases.length == 0 ? "40px" : "5px", "auto", teamTwo.aliases.length == 0 ? "20px" : "65px"]}>
                                    <Box></Box>
                                    <TeamName team={teamTwo.team} size={"medium"} color=""></TeamName>
                                    <Box direction="row" gap="small">
                                        {teamTwo && teamTwo.aliases.map((a) => <AliasTile key={a.aliasID} alias={a.alias} size="small"></AliasTile>)}
                                    </Box>
                                </Grid>
                            )}
                            {teamTwo && (
                                <Box align="end" justify="center" fill>
                                    <img src={teamTwo.team.logoUri} alt="" style={{ maxHeight: "60px" }} />
                                    {size == "small" && <TeamName team={teamTwo.team} size={"medium"} color=""></TeamName>}
                                </Box>
                            )}
                        </Grid>
                    )}
                    {match.participants.length > 2 && (
                        <Box direction="row" justify="evenly">
                            {match.participants.map((participant) => (
                                <Box direction="row" key={participant.team.id} align="center" gap="small">
                                    <Box align="center">
                                        <Image width="60px" fit="cover" src={participant.team.logoUri} />
                                        <TeamName team={participant.team} size={"medium"} color=""></TeamName>
                                    </Box>
                                    <RenderOutcome outcome={first(orderBy(participant.outcomes, ["timestamp"], ["desc"]))} size={size}></RenderOutcome>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
                {/* Winner Banner */}
                {hasWinner && (
                    <Box 
                        pad="small" 
                        fill="horizontal" 
                        background={{
                            color: "#00C781",
                            opacity: "strong"
                        }}
                        style={{ 
                            boxShadow: "0 4px 20px rgba(0, 199, 129, 0.6)",
                            borderLeft: "4px solid #00FF9D",
                            borderRight: "4px solid #00FF9D"
                        }}
                        animation={{ type: "fadeIn", duration: 500 }}
                    >
                        <Box direction="row" align="center" justify="center" gap="small">
                            <FontAwesomeIcon icon={["fad", "trophy-star"]} style={{ color: "#FFD700", fontSize: "20px" }} />
                            <Text 
                                size="medium" 
                                weight="bold" 
                                color="white" 
                                style={{ 
                                    textTransform: "uppercase", 
                                    letterSpacing: "2px",
                                    textShadow: "0 2px 4px rgba(0,0,0,0.3)"
                                }}
                            >
                                Winner: {teamOneWon ? teamOne?.team?.name : teamTwo?.team?.name}
                            </Text>
                            <FontAwesomeIcon icon={["fad", "trophy-star"]} style={{ color: "#FFD700", fontSize: "20px" }} />
                        </Box>
                    </Box>
                )}
                {match.tournamentID && (
                    <Box pad="xsmall" fill="horizontal">
                        <Text size="small" style={{ textTransform: "uppercase" }}>
                            {match.tournament.name}
                        </Text>
                    </Box>
                )}
                {match.seasonID && (
                    <Box pad="xsmall" fill="horizontal">
                        <Text size="small" style={{ textTransform: "uppercase" }}>
                            {match.league.name} - {match.season.name}
                        </Text>
                    </Box>
                )}
                {match.outcomes.length == 0 && (
                    <Box align="center" justify="center" style={{ height: "110px" }}>
                        <Box pad={"small"}>
                            <Text size="large" color="background-front" style={{ fontWeight: "bold", textTransform: "uppercase" }}>
                                VS
                            </Text>
                        </Box>
                    </Box>
                )}
                {match.outcomes.length > 0 && (
                    <Box direction="row" gap="small" align="center" justify="center" style={{ height: "110px" }} margin={{ top: "small" }}>
                        <Box align="center" gap="xsmall">
                            {teamOneWon && (
                                <Box 
                                    background="#00C781" 
                                    pad={{ horizontal: "small", vertical: "xxsmall" }} 
                                    round="xsmall"
                                    style={{ boxShadow: "0 0 10px rgba(0, 199, 129, 0.5)" }}
                                >
                                    <Text size="xsmall" weight="bold" color="white" style={{ textTransform: "uppercase", letterSpacing: "1px" }}>
                                        Winner
                                    </Text>
                                </Box>
                            )}
                            {teamOneOutcome?.outcomeType === "Loss" && (
                                <Box 
                                    background="rgba(255, 170, 21, 0.3)" 
                                    pad={{ horizontal: "small", vertical: "xxsmall" }} 
                                    round="xsmall"
                                    border={{ color: "#FFAA15", size: "1px" }}
                                >
                                    <Text size="xsmall" weight="bold" color="#FFAA15" style={{ textTransform: "uppercase", letterSpacing: "1px" }}>
                                        Loss
                                    </Text>
                                </Box>
                            )}
                            <RenderOutcome outcome={teamOneOutcome} size={size}></RenderOutcome>
                        </Box>
                        <FontAwesomeIcon icon={["fad", "hyphen"]}></FontAwesomeIcon>
                        <Box align="center" gap="xsmall">
                            {teamTwoWon && (
                                <Box 
                                    background="#00C781" 
                                    pad={{ horizontal: "small", vertical: "xxsmall" }} 
                                    round="xsmall"
                                    style={{ boxShadow: "0 0 10px rgba(0, 199, 129, 0.5)" }}
                                >
                                    <Text size="xsmall" weight="bold" color="white" style={{ textTransform: "uppercase", letterSpacing: "1px" }}>
                                        Winner
                                    </Text>
                                </Box>
                            )}
                            {teamTwoOutcome?.outcomeType === "Loss" && (
                                <Box 
                                    background="rgba(255, 170, 21, 0.3)" 
                                    pad={{ horizontal: "small", vertical: "xxsmall" }} 
                                    round="xsmall"
                                    border={{ color: "#FFAA15", size: "1px" }}
                                >
                                    <Text size="xsmall" weight="bold" color="#FFAA15" style={{ textTransform: "uppercase", letterSpacing: "1px" }}>
                                        Loss
                                    </Text>
                                </Box>
                            )}
                            <RenderOutcome outcome={teamTwoOutcome} size={size} reverse></RenderOutcome>
                        </Box>
                    </Box>
                )}
                {match.startDate && match.startDate != "0001-01-01T00:00:00" && (
                    <Box pad={{ top: "90px" }}>
                        <Text size="small" color={!!match.splashUri ? "white" : "background-front"}>
                            {formatRelative(parseJSON(match.startDate), new Date())}
                        </Text>
                    </Box>
                )}
            </Stack>
        </Link>
    );
};
